/**
 * Layout — composes the entire world.
 *
 * Owns:
 *   - Terrain (procedural heightmap)
 *   - All stages (placed at zone centers)
 *   - Landmarks (Arch, Fountain, Ferris Wheel, Plaza Barns)
 *   - Vegetation scatter
 *   - Campground tent scatter
 *   - Vendor row + porta-potties + fence perimeter
 *   - Physics colliders for stage perimeters
 *
 * Returns a record of stage handles + animatable systems so the main loop
 * can update them each frame.
 */

import * as THREE from 'three';
import { Terrain, HeightField } from '@/world/Terrain';
import { Vegetation } from '@/world/Vegetation';
import { Campground } from '@/world/Campground';
import { Props } from '@/world/Props';
import { Atmosphere } from '@/world/Atmosphere';
import { StageLights } from '@/world/StageLights';
import { Paths } from '@/world/Paths';
import { Arch } from '@/world/landmarks/Arch';
import { Fountain } from '@/world/landmarks/Fountain';
import { FerrisWheel } from '@/world/landmarks/FerrisWheel';
import { PlazaBarn } from '@/world/landmarks/PlazaBarn';
import { WhatStage } from '@/world/stages/WhatStage';
import { WhichStage } from '@/world/stages/WhichStage';
import { ThisTent, ThatTent } from '@/world/stages/Tents';
import { OtherStage } from '@/world/stages/OtherStage';
import { WoodsStage } from '@/world/stages/WoodsStage';
import type { StageHandle } from '@/world/stages/StageBase';
import type { PhysicsWorld } from '@/core/PhysicsWorld';
import { ZONES } from '@shared/zones';

export interface SpawnPoint {
  x: number;
  z: number;
  yaw: number;
}

export interface BuiltWorld {
  terrain: Terrain;
  stages: Record<string, StageHandle>;
  animated: { update: (dt: number) => void }[];
  scene: THREE.Group;
  /** Computed spawn positions per zone, audience-side and facing the stage. */
  spawns: Record<string, SpawnPoint>;
}

export async function buildWorld(physics: PhysicsWorld): Promise<BuiltWorld> {
  const root = new THREE.Group();
  root.name = 'World';

  // Terrain — try to load real USGS 3DEP DEM, fall back to procedural noise
  // if the binary is missing (e.g. someone forgot to run scripts/fetch-terrain.mjs).
  const WORLD_SIZE = 2200;
  let terrain: Terrain;
  try {
    const hf = await HeightField.fromBinary('/terrain/great-stage-park.bin', WORLD_SIZE);
    terrain = new Terrain(hf);
    // eslint-disable-next-line no-console
    console.info('[world] Loaded real USGS terrain', hf.realMinElev.toFixed(1), '-', hf.realMaxElev.toFixed(1), 'm');
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[world] USGS heightmap missing, using procedural fallback', err);
    terrain = Terrain.build({ size: WORLD_SIZE, resolution: 257, seed: 42 });
  }

  // Flatten stage footprints before generating mesh data
  const stageFootprints: { x: number; z: number; r: number }[] = [];
  const flatten = (zoneId: string, radius: number, height: number) => {
    const zone = ZONES[zoneId];
    if (!zone) return;
    terrain.heightField.flattenAt(zone.center[0], zone.center[1], radius, height, 1.4);
    stageFootprints.push({ x: zone.center[0], z: zone.center[1], r: radius });
  };
  flatten('arch', 40, 1);
  flatten('centeroo-core', 80, 0);
  flatten('what-stage', 110, -1);
  flatten('which-stage', 80, 0);
  flatten('this-that-tents', 60, 0.5);
  flatten('other-stage', 80, 0);
  flatten('woods', 45, 0.5);
  flatten('outeroo-pods', 120, 1);
  flatten('ferris', 40, 0);
  terrain.rebuild();

  root.add(terrain.mesh);

  // ─── Stages ───
  const stages: Record<string, StageHandle> = {};
  const spawns: Record<string, SpawnPoint> = {};

  /**
   * Place a stage and compute the audience spawn point. The stage's local
   * +Z axis is the "front" (the audience side). The audience spawn is
   * `audienceDistance` meters along that direction in world space, and the
   * player faces back at the stage center.
   */
  const placeStage = (
    handle: StageHandle,
    zoneId: string,
    yawDeg: number,
    audienceDistance = 38
  ) => {
    const zone = ZONES[zoneId];
    if (!zone) return;
    const [cx, cz] = zone.center;
    const y = terrain.heightAt(cx, cz);
    const yawRad = (yawDeg * Math.PI) / 180;

    handle.group.position.set(cx, y, cz);
    handle.group.rotation.y = yawRad;
    handle.position.set(cx, y, cz);
    root.add(handle.group);
    stages[handle.id] = handle;

    // World direction of local +Z under Y rotation = (sin, cos).
    const dirX = Math.sin(yawRad);
    const dirZ = Math.cos(yawRad);
    const sx = cx + dirX * audienceDistance;
    const sz = cz + dirZ * audienceDistance;
    // Yaw to look from spawn back to stage center.
    spawns[zoneId] = {
      x: sx,
      z: sz,
      yaw: Math.atan2(-(cx - sx), -(cz - sz))
    };
  };

  placeStage(WhatStage.build(), 'what-stage', 90, 90);
  placeStage(WhichStage.build(), 'which-stage', 200, 75);

  // Add a colored point light at each placed stage so LEDs and trim glow
  // outward. Color matches the stage's accent.
  const STAGE_LIGHT_COLORS: Record<string, number> = {
    'what-stage': 0xff5f6d,
    'which-stage': 0x7ce0a3,
    'this-tent': 0xff5f6d,
    'that-tent': 0x56c2ff,
    'other-stage': 0xbd72ff,
    'woods': 0xffcb6b
  };
  // Apply later after all stages are placed.
  placeStage(ThisTent.build(), 'this-that-tents', 0, 40);
  const that = ThatTent.build();
  // Offset That Tent next to This Tent
  const ttZone = ZONES['this-that-tents'];
  const tx = ttZone.center[0] + 35;
  const tz = ttZone.center[1];
  const ty = terrain.heightAt(tx, tz);
  that.group.position.set(tx, ty, tz);
  that.position.set(tx, ty, tz);
  root.add(that.group);
  stages[that.id] = that;

  placeStage(OtherStage.build(), 'other-stage', 180, 75);
  placeStage(WoodsStage.build(), 'woods', 45, 45);

  // Drop a tinted point light at each stage for color spill.
  for (const [stageId, color] of Object.entries(STAGE_LIGHT_COLORS)) {
    const stage = stages[stageId];
    if (!stage) continue;
    const light = new THREE.PointLight(color, 6.0, 80, 1.6);
    light.position.set(stage.position.x, stage.position.y + 8, stage.position.z);
    root.add(light);
  }

  // ─── Landmarks ───
  const animated: { update: (dt: number) => void }[] = [];

  // Animated spotlight beam rigs above each stage truss
  const stageLights = new StageLights(Object.values(stages));
  root.add(stageLights.group);
  animated.push({
    update: (_dt: number) => stageLights.update(performance.now() * 0.001)
  });

  // Bonnaroo Arch at the entrance — scaled up so it reads as iconic
  const archZone = ZONES.arch;
  const arch = Arch.build({ classic: true, radius: 26, thickness: 3.0, depth: 5.0 });
  arch.position.set(archZone.center[0], terrain.heightAt(archZone.center[0], archZone.center[1]), archZone.center[1]);
  root.add(arch);

  // Fountain in Centeroo core
  const coreZone = ZONES['centeroo-core'];
  const fountain = Fountain.build();
  fountain.group.position.set(coreZone.center[0], terrain.heightAt(coreZone.center[0], coreZone.center[1]), coreZone.center[1]);
  root.add(fountain.group);
  animated.push({ update: fountain.update });

  // Ferris Wheel
  const ferrisZone = ZONES.ferris;
  const ferris = FerrisWheel.build();
  ferris.group.position.set(ferrisZone.center[0], terrain.heightAt(ferrisZone.center[0], ferrisZone.center[1]), ferrisZone.center[1]);
  root.add(ferris.group);
  animated.push({ update: ferris.update });

  // Plaza Barns — six clustered around the Outeroo zone
  const podZone = ZONES['outeroo-pods'];
  const barnColors = ['#ff5f6d', '#ffc371', '#7ce0a3', '#56c2ff', '#bd72ff', '#ff8ec0'];
  for (let i = 0; i < 6; i++) {
    const angle = (i / 6) * Math.PI * 2;
    const r = 70;
    const bx = podZone.center[0] + Math.cos(angle) * r;
    const bz = podZone.center[1] + Math.sin(angle) * r;
    const by = terrain.heightAt(bx, bz);
    const barn = PlazaBarn.build({
      podNumber: i + 1,
      accentColor: barnColors[i]
    });
    barn.position.set(bx, by, bz);
    barn.rotation.y = angle + Math.PI;
    root.add(barn);
  }

  // ─── Vendors + porta-potties ───
  root.add(
    Props.buildVendorRow(terrain, {
      startX: coreZone.center[0] - 50,
      startZ: coreZone.center[1] + 30,
      endX: coreZone.center[0] + 50,
      endZ: coreZone.center[1] + 30,
      count: 8
    })
  );
  root.add(Props.buildPortaPottyRow(terrain, coreZone.center[0] - 30, coreZone.center[1] - 70, 6));
  root.add(Props.buildPortaPottyRow(terrain, podZone.center[0] + 50, podZone.center[1] - 90, 6));

  // ─── Vegetation scatter ───
  root.add(
    Vegetation.build(terrain, {
      size: 2100,
      count: 900,
      exclude: [
        ...stageFootprints.map((f) => ({ ...f, r: f.r + 14 })),
        // Keep the central pasture mostly clear
        { x: coreZone.center[0], z: coreZone.center[1], r: 180 },
        { x: ZONES['what-stage'].center[0], z: ZONES['what-stage'].center[1], r: 220 }
      ]
    })
  );

  // ─── Grass tufts on audience fields ───
  root.add(
    Vegetation.buildGrassTufts(terrain, [
      { x: ZONES['what-stage'].center[0], z: ZONES['what-stage'].center[1], r: 180, count: 600 },
      { x: ZONES['which-stage'].center[0], z: ZONES['which-stage'].center[1], r: 140, count: 400 },
      { x: ZONES['other-stage'].center[0], z: ZONES['other-stage'].center[1], r: 140, count: 400 },
      { x: coreZone.center[0], z: coreZone.center[1], r: 150, count: 500 },
      { x: archZone.center[0], z: archZone.center[1], r: 70, count: 200 }
    ])
  );

  // ─── Dirt paths connecting key locations ───
  root.add(
    Paths.build(terrain, [
      // Arch → Centeroo (the main entrance walk)
      [
        { x: archZone.center[0], z: archZone.center[1], width: 6 },
        { x: 0, z: 110, width: 6 },
        { x: coreZone.center[0], z: coreZone.center[1], width: 8 }
      ],
      // Centeroo → What Stage
      [
        { x: coreZone.center[0], z: coreZone.center[1], width: 8 },
        { x: -150, z: 300, width: 5 },
        { x: ZONES['what-stage'].center[0] + 60, z: ZONES['what-stage'].center[1], width: 5 }
      ],
      // Centeroo → Which Stage
      [
        { x: coreZone.center[0], z: coreZone.center[1], width: 8 },
        { x: 60, z: 300, width: 4 },
        { x: ZONES['which-stage'].center[0], z: ZONES['which-stage'].center[1] + 50, width: 4 }
      ],
      // Centeroo → This/That Tents
      [
        { x: coreZone.center[0], z: coreZone.center[1], width: 6 },
        { x: 180, z: 220, width: 4 },
        { x: ZONES['this-that-tents'].center[0], z: ZONES['this-that-tents'].center[1], width: 4 }
      ],
      // Centeroo → Other Stage
      [
        { x: coreZone.center[0], z: coreZone.center[1], width: 6 },
        { x: 200, z: 140, width: 4 },
        { x: ZONES['other-stage'].center[0], z: ZONES['other-stage'].center[1] - 50, width: 4 }
      ],
      // Arch → Outeroo
      [
        { x: archZone.center[0], z: archZone.center[1] - 20, width: 5 },
        { x: -60, z: -180, width: 4 },
        { x: podZone.center[0], z: podZone.center[1], width: 5 }
      ],
      // Outeroo → Woods
      [
        { x: podZone.center[0], z: podZone.center[1], width: 4 },
        { x: -240, z: -280, width: 4 },
        { x: ZONES['woods'].center[0], z: ZONES['woods'].center[1], width: 4 }
      ],
      // Outeroo → Ferris
      [
        { x: podZone.center[0], z: podZone.center[1], width: 4 },
        { x: 60, z: -350, width: 4 },
        { x: ZONES['ferris'].center[0], z: ZONES['ferris'].center[1], width: 4 }
      ]
    ])
  );

  // ─── Atmosphere: clouds, flags, haze ───
  root.add(Atmosphere.buildClouds());

  // Festival flags scattered through every audience area
  const flagCenters = Object.values(stages).map((s) => ({
    x: s.position.x,
    z: s.position.z,
    r: 50,
    count: 18
  }));
  // Plus a ring of flags through Centeroo and around the Arch
  flagCenters.push({ x: coreZone.center[0], z: coreZone.center[1], r: 90, count: 30 });
  flagCenters.push({ x: archZone.center[0], z: archZone.center[1], r: 50, count: 16 });
  root.add(Atmosphere.buildFlags(terrain, flagCenters));

  // Subtle ground haze near each major audience zone
  root.add(
    Atmosphere.buildHaze(
      Object.values(stages).map((s) => ({ x: s.position.x, z: s.position.z, r: 70 }))
    )
  );

  // ─── Campground scatter ───
  root.add(
    Campground.build(terrain, [
      { x: podZone.center[0] - 80, z: podZone.center[1] + 60, r: 80, count: 90 },
      { x: podZone.center[0] + 100, z: podZone.center[1] - 30, r: 90, count: 110 },
      { x: podZone.center[0] - 60, z: podZone.center[1] - 120, r: 70, count: 80 },
      { x: podZone.center[0] + 200, z: podZone.center[1] + 100, r: 100, count: 130 }
    ])
  );

  // ─── Physics colliders ───
  // Simple cylinder colliders for stage decks so players can't walk through.
  for (const stage of Object.values(stages)) {
    physics.addCylinder(stage.position.x, stage.position.z, 14, 4);
  }
  // Fountain
  physics.addCylinder(coreZone.center[0], coreZone.center[1], 6.5, 2);
  // Ferris hub
  physics.addCylinder(ferrisZone.center[0], ferrisZone.center[1], 7, 4);

  // Non-stage spawn points. yawTowards computes the yaw a player at (px,pz)
  // needs to look at a target (tx,tz). Three.js camera forward = -Z so
  // forward = (-sin(yaw), -cos(yaw)).
  const yawTowards = (px: number, pz: number, tx: number, tz: number): number => {
    const dx = tx - px;
    const dz = tz - pz;
    return Math.atan2(-dx, -dz);
  };

  // Arch: stand 30m south of the arch (in +Z direction), look toward arch.
  {
    const px = archZone.center[0];
    const pz = archZone.center[1] + 30;
    spawns['arch'] = {
      x: px,
      z: pz,
      yaw: yawTowards(px, pz, archZone.center[0], archZone.center[1])
    };
  }
  // Centeroo: stand southeast of the fountain, look at it.
  {
    const px = coreZone.center[0] + 22;
    const pz = coreZone.center[1] + 22;
    spawns['centeroo-core'] = {
      x: px,
      z: pz,
      yaw: yawTowards(px, pz, coreZone.center[0], coreZone.center[1])
    };
  }
  // Ferris Wheel: stand southeast of the wheel for a great frame.
  {
    const px = ferrisZone.center[0] + 22;
    const pz = ferrisZone.center[1] + 22;
    spawns['ferris'] = {
      x: px,
      z: pz,
      yaw: yawTowards(px, pz, ferrisZone.center[0], ferrisZone.center[1])
    };
  }
  // Outeroo: stand at zone center, face north toward Centeroo.
  {
    const px = podZone.center[0];
    const pz = podZone.center[1];
    spawns['outeroo-pods'] = {
      x: px,
      z: pz,
      yaw: yawTowards(px, pz, 0, 0)
    };
  }

  return { terrain, stages, animated, scene: root, spawns };
}
